import { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/utils/cn';
import { StepIndicator } from './StepIndicator';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';

/**
 * WizardModal Component
 *
 * A modal designed for multi-step wizards with:
 * - Fixed header with title and close button
 * - Fixed step indicator with description
 * - Scrollable content area
 * - Fixed footer for navigation buttons
 */

export interface WizardStep {
  id: string;
  label: string;
}

interface WizardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Array of step objects with id and label */
  steps: WizardStep[];
  /** Current step index (0-based) */
  currentStep: number;
  /** Called when user clicks on a step */
  onStepClick?: (index: number) => void;
  /** Description text shown below step indicator */
  stepDescription?: string;
  /** Main content (scrollable) */
  children: ReactNode;
  /** Footer content (fixed at bottom) */
  footer: ReactNode;
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Additional class for the modal */
  className?: string;
  /** Prevent closing the modal */
  preventClose?: boolean;
}

export const WizardModal = ({
  open,
  onOpenChange,
  title,
  steps,
  currentStep,
  onStepClick,
  stepDescription,
  children,
  footer,
  size = 'xl',
  className,
  preventClose = false,
}: WizardModalProps) => {
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && preventClose) {
      return;
    }
    onOpenChange(newOpen);
  };

  const stackDepth = useModalStackDepth(open);
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(stackDepth);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Overlay and Content must be siblings (not nested) — nesting Content inside
            Overlay confuses Radix's focus-trap sentinel logic and causes the capture-phase
            keydown handler to eat non-Tab key events (e.g. Space) inside inputs. */}
        <Dialog.Overlay
          style={{ zIndex: backdropZ }}
          className={cn(
            'fixed inset-0 bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out'
          )}
        />
        <Dialog.Content
          style={{ zIndex: contentZ }}
          className={cn(
            'fixed inset-0 flex items-center justify-center',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out'
          )}
        >
          <div
            className={cn(
              'w-[95vw]',
              sizeClasses[size],
              'bg-white rounded-2xl shadow-xl',
              'flex flex-col',
              'max-h-[calc(100vh-8rem)]',
              'focus:outline-none',
              className
            )}
            // Stop React-synthetic keydown events from bubbling out of the modal
            // into ancestor DndContext/KeyboardSensor listeners (e.g. on Dashboard).
            // dnd-kit's KeyboardSensor intercepts Space at the React-tree level and
            // calls preventDefault(), preventing inputs from typing spaces.
            // Radix's own keyboard handling (Esc, Tab, focus-trap) uses
            // DOM-level document.addEventListener, so it is unaffected by this.
            onKeyDown={(e) => e.stopPropagation()}
          >
            {/* Accessibility */}
            <VisuallyHidden>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Dialog.Description>{title} wizard dialog</Dialog.Description>
            </VisuallyHidden>

            {/* Fixed Header: Title + Close */}
            <div className="shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl">
              <Dialog.Title className="text-base sm:text-xl font-bold text-gray-900">
                {title}
              </Dialog.Title>
              {!preventClose && (
                <Dialog.Close
                  className={cn(
                    'w-8 h-8 rounded-full',
                    'flex items-center justify-center',
                    'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                    'transition-colors duration-200',
                    'focus:outline-none'
                  )}
                  aria-label="Close"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Dialog.Close>
              )}
            </div>

            {/* Fixed Step Indicator */}
            <div className="shrink-0 bg-white border-b border-gray-100 px-3 sm:px-6 py-3 sm:py-4">
              <StepIndicator
                steps={steps}
                currentStepIndex={currentStep}
                onStepClick={onStepClick}
                className="mb-1.5 sm:mb-2"
              />
              {stepDescription && (
                <p className="text-xs sm:text-sm text-gray-500 text-center mt-1">{stepDescription}</p>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 min-h-0">
              {children}
            </div>

            {/* Fixed Footer */}
            <div className="shrink-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 rounded-b-2xl">
              {footer}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
